import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Button, Col, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { formatearFecha, validarRangoFechas } from '../../helpers/dateValidation'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import * as listaVentaService from '../servicios/listaVentaService'
import {
  BuscarPublicacionesPorLista,
  aplicarVariacionPrecio
} from '../servicios/publicacionesService'
import styles from './ListaVenta.module.css'

const DECIMAL_PLACES = 2
const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm'

export const ModalEditarItemsLista = ({ show, onHide, listaVenta, onActualizar, idVendedor }) => {
  const [items, setItems] = useState([])
  const [cargando, setCargando] = useState(false)
  const [valores, setValores] = useState({
    variacionPrecio: '',
    operadorVariacion: '+',
    porcDcto: '',
    ofertaDesde: '',
    ofertaHasta: ''
  })
  const [tipoModificacion, setTipoModificacion] = useState('')
  const [variacionesIndividuales, setVariacionesIndividuales] = useState({})
  const [erroresValidacion, setErroresValidacion] = useState({})

  useEffect(() => {
    if (show && listaVenta?.idListaVenta) {
      const fechaHoy = formatearFecha(new Date(), DATE_TIME_FORMAT)

      cargarItems()
      setValores({
        variacionPrecio: '',
        operadorVariacion: '+',
        porcDcto: '',
        ofertaDesde: fechaHoy,
        ofertaHasta: listaVenta.ofertaHasta
          ? formatearFecha(listaVenta.ofertaHasta, DATE_TIME_FORMAT)
          : ''
      })
      setTipoModificacion('')
      setVariacionesIndividuales({})
      setErroresValidacion({})
    }
  }, [show, listaVenta])

  const getPrecioBase = ({ precioUnitario, precio }) => {
    const val = precioUnitario ?? precio
    if (val === null || val === undefined) return null

    const num = Number(val)
    return Number.isNaN(num) ? null : num
  }

  const cargarItems = async () => {
    try {
      setCargando(true)
      const { data } = await BuscarPublicacionesPorLista(listaVenta.idListaVenta)
      const itemsFiltrados = (data || []).filter((item) => {
        if (!idVendedor) {
          return true
        }

        const vendedorItem = item.idVendedor ?? listaVenta?.idVendedor ?? null
        if (vendedorItem === null || vendedorItem === undefined) {
          return true
        }
        return vendedorItem === idVendedor
      })
      const itemsMapeados = itemsFiltrados.map((item) => ({
        ...item,
        porcDcto: item.porcDcto || '',
        variacionPrecio: item.variacionPrecio || '',
        operadorVariacion: item.operadorVariacion || '+'
      }))
      setItems(itemsMapeados)
    } catch (error) {
      const mensaje = 'Error al cargar los items de la lista'
      toastErrorLog(error, mensaje, 'listaEnvio')
    } finally {
      setCargando(false)
    }
  }

  const validarFechaDesde = (fechaDesde) => {
    return validarRangoFechas(new Date(), fechaDesde, 'day')
  }

  const validarFechaHasta = (fechaHasta, fechaDesde) => {
    return validarRangoFechas(fechaDesde, fechaHasta)
  }

  const validarFechaHastaContraVigenciaLista = (fechaHasta) => {
    if (!fechaHasta || !listaVenta?.vigenciaHasta) {
      return true
    }

    return validarRangoFechas(fechaHasta, listaVenta.vigenciaHasta)
  }

  const actualizarErroresOferta = (nuevosErrores, fechaDesde, fechaHasta) => {
    const esFechaDesdeValida = validarFechaDesde(fechaDesde)
    if (!esFechaDesdeValida) {
      nuevosErrores.ofertaDesde = 'La fecha desde no puede ser menor a hoy'
    } else {
      delete nuevosErrores.ofertaDesde
    }

    const esFechaHastaValida = validarFechaHasta(fechaHasta, fechaDesde)
    if (!esFechaHastaValida) {
      nuevosErrores.ofertaHasta = 'La fecha hasta no puede ser menor que la fecha desde'
    } else {
      delete nuevosErrores.ofertaHasta
    }

    if (
      tipoModificacion === 'descuento' &&
      esFechaHastaValida &&
      !validarFechaHastaContraVigenciaLista(fechaHasta)
    ) {
      nuevosErrores.ofertaHasta = 'La fecha hasta no puede superar la vigencia de la lista'
    }
  }

  const handleCambio = (campo, valor) => {
    let valorFinal = valor
    const nuevosErrores = { ...erroresValidacion }

    switch (campo) {
      case 'variacionPrecio': {
        const num = parseFloat(valor)
        valorFinal = isNaN(num) ? '' : Math.min(Math.max(num, 1), 100).toString()
        setTipoModificacion(valorFinal !== '' ? 'variacion' : '')
        break
      }
      case 'porcDcto': {
        const num = parseFloat(valor)
        valorFinal = isNaN(num) ? '' : Math.min(Math.max(num, 0), 100).toString()
        setTipoModificacion(valorFinal !== '' ? 'descuento' : '')
        break
      }
      case 'ofertaDesde': {
        actualizarErroresOferta(nuevosErrores, valor, valores.ofertaHasta)
        break
      }
      case 'ofertaHasta': {
        actualizarErroresOferta(nuevosErrores, valores.ofertaDesde, valor)
        break
      }
      default:
        break
    }

    setValores((prev) => ({ ...prev, [campo]: valorFinal }))
    setErroresValidacion(nuevosErrores)
  }

  const handleVariacionIndividual = (idPublicacion, valor) => {
    setVariacionesIndividuales({
      ...variacionesIndividuales,
      [idPublicacion]: valor
    })
  }

  const validarFechasOferta = (validarContraVigenciaLista = false) => {
    if (!valores.ofertaDesde) {
      toastErrorLog('', 'Agregue fecha desde')
      return false
    }
    if (!valores.ofertaHasta) {
      toastErrorLog('', 'Agregue fecha hasta')
      return false
    }

    const esFechaDesdeValida = validarFechaDesde(valores.ofertaDesde)
    if (!esFechaDesdeValida) {
      toastErrorLog('', 'La fecha desde no puede ser menor a hoy')
      return false
    }

    const esFechaHastaValida = validarFechaHasta(valores.ofertaHasta, valores.ofertaDesde)
    if (!esFechaHastaValida) {
      toastErrorLog('', 'La fecha hasta no puede ser menor que la fecha desde')
      return false
    }

    if (validarContraVigenciaLista && !validarFechaHastaContraVigenciaLista(valores.ofertaHasta)) {
      toastErrorLog('', 'La fecha hasta no puede superar la vigencia de la lista')
      return false
    }

    return true
  }

  const handleAplicarDescuento = async () => {
    try {
      if (!validarFechasOferta(true)) {
        return
      }

      const actualizacion = {
        idListaVenta: listaVenta.idListaVenta,
        porcDcto:
          valores.porcDcto !== '' && valores.porcDcto !== null
            ? parseFloat(valores.porcDcto)
            : null,
        ofertaDesde: valores.ofertaDesde ? `${valores.ofertaDesde}:00` : null,
        ofertaHasta: valores.ofertaHasta ? `${valores.ofertaHasta}:00` : null
      }

      await listaVentaService.actualizarDescuentoLista(actualizacion)

      toast.success('Descuento y fechas actualizadas correctamente')
      await cargarItems()
      onActualizar?.()
    } catch (error) {
      const mensaje = 'Error al guardar cambios'
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const handleAplicarVariacionPrecio = async () => {
    try {
      if (!validarFechasOferta()) {
        return
      }
      const idUsuario = parseInt(localStorage.getItem('idUsuario'))
      const desdeNuevo = valores.ofertaDesde ? `${valores.ofertaDesde}:00` : null
      const hastaNuevo = valores.ofertaHasta ? `${valores.ofertaHasta}:00` : null
      const idlistaVenta = listaVenta.idListaVenta
      const variacion = parseInt(valores.variacionPrecio)

      const operador = valores.operadorVariacion === '+' ? 1 : 0
      const publicaciones = items.map((item) => {
        let variacionPrecio = valores.variacionPrecio
        let operadorVariacion = valores.operadorVariacion
        if (variacionesIndividuales[item.idPublicacion] !== undefined) {
          if (typeof variacionesIndividuales[item.idPublicacion] === 'object') {
            variacionPrecio =
              variacionesIndividuales[item.idPublicacion].variacionPrecio ?? valores.variacionPrecio
            operadorVariacion =
              variacionesIndividuales[item.idPublicacion].operadorVariacion ??
              valores.operadorVariacion
          } else {
            variacionPrecio = variacionesIndividuales[item.idPublicacion]
          }
        }

        const basePrecio = getPrecioBase(item)
        let precioNuevo = basePrecio
        if (basePrecio !== null && variacionPrecio !== '') {
          const variacionNum = Number(variacionPrecio) || 0
          const factor = operadorVariacion === '-' ? -variacionNum / 100 : variacionNum / 100
          precioNuevo = basePrecio * (1 + factor)
        }
        return {
          idPublicacionVieja: item.idPublicacion,
          precioNuevo,
          desdeNuevo,
          hastaNuevo,
          idUsuario,
          idlistaVenta,
          variacion,
          operador
        }
      })

      for (const pub of publicaciones) {
        await aplicarVariacionPrecio(pub)
      }

      toast.success('Variaciones aplicadas correctamente')
      await cargarItems()
      onActualizar?.()
    } catch (error) {
      const mensaje = 'Error al guardar cambios'
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Publicaciones de la lista: {listaVenta?.nombre || 'Lista'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-4 p-3 border rounded bg-light">
          <h6 className="mb-3">Aplicar cambios a todas las publicaciones de la lista</h6>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Operador</Form.Label>
                <Form.Select
                  value={valores.operadorVariacion}
                  onChange={(e) => handleCambio('operadorVariacion', e.target.value)}
                  disabled={tipoModificacion === 'descuento' || items.length === 0}
                >
                  <option value="+">+ (Aumentar)</option>
                  <option value="-">- (Disminuir)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Variación Precio % (1-100)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="1"
                  max="100"
                  placeholder={
                    tipoModificacion === 'descuento'
                      ? 'Bloqueado'
                      : 'Completar para modificar precio'
                  }
                  value={valores.variacionPrecio}
                  onChange={(e) => handleCambio('variacionPrecio', e.target.value)}
                  disabled={tipoModificacion === 'descuento' || items.length === 0}
                />
              </Form.Group>
            </Col>
            {(tipoModificacion === 'variacion' || tipoModificacion === 'descuento') && (
              <>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {tipoModificacion === 'variacion'
                        ? 'Vigencia de publicación desde'
                        : 'Oferta desde'}
                    </Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={valores.ofertaDesde}
                      onChange={(e) => handleCambio('ofertaDesde', e.target.value)}
                      disabled={items.length === 0}
                      isInvalid={!!erroresValidacion.ofertaDesde}
                    />
                    {erroresValidacion.ofertaDesde && (
                      <Form.Control.Feedback type="invalid">
                        {erroresValidacion.ofertaDesde}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {tipoModificacion === 'variacion'
                        ? 'Vigencia de publicación hasta'
                        : 'Oferta hasta'}
                    </Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={valores.ofertaHasta}
                      onChange={(e) => handleCambio('ofertaHasta', e.target.value)}
                      disabled={items.length === 0}
                      isInvalid={!!erroresValidacion.ofertaHasta}
                    />
                    {erroresValidacion.ofertaHasta && (
                      <Form.Control.Feedback type="invalid">
                        {erroresValidacion.ofertaHasta}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </>
            )}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Descuento % (0-100)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder={
                    tipoModificacion === 'variacion'
                      ? 'Bloqueado'
                      : 'Completar para aplicar descuento'
                  }
                  value={valores.porcDcto}
                  onChange={(e) => handleCambio('porcDcto', e.target.value)}
                  disabled={tipoModificacion === 'variacion' || items.length === 0}
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="success"
              onClick={handleAplicarDescuento}
              disabled={
                tipoModificacion !== 'descuento' ||
                items.length === 0 ||
                !!erroresValidacion.ofertaDesde ||
                !!erroresValidacion.ofertaHasta
              }
            >
              <i className="bi bi-tags me-2"></i>
              Aplicar descuento
            </Button>
            <Button
              variant="primary"
              onClick={handleAplicarVariacionPrecio}
              disabled={
                tipoModificacion !== 'variacion' ||
                items.length === 0 ||
                !!erroresValidacion.ofertaDesde ||
                !!erroresValidacion.ofertaHasta
              }
            >
              <i className="bi bi-arrow-left-right me-2"></i>
              Aplicar variación de precio
            </Button>
          </div>
          <div className="mt-2">
            <span>
              última variación de precios aplicada:{' '}
              {listaVenta?.variacionPrecio
                ? (listaVenta.signo ? '+ ' : '- ') + listaVenta.variacionPrecio
                : 'N/A'}{' '}
              | Vigencia:{' '}
              {listaVenta?.variacionPrecioDesde
                ? formatearFecha(listaVenta.variacionPrecioDesde)
                : 'N/A'}{' '}
              -{' '}
              {listaVenta?.variacionPrecioHasta
                ? formatearFecha(listaVenta.variacionPrecioHasta)
                : 'N/A'}
            </span>
          </div>
        </div>

        {cargando ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Cargando...</span>
            </div>
          </div>
        ) : items.length > 0 ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Previsualización de publicaciones ({items.length})</h6>
            </div>
            <div className={styles.tableContainer}>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th className={styles.thWidth60px}>ID</th>
                    <th>Título</th>
                    <th className={styles.thWidth120px}>Precio Actual</th>
                    <th className={styles.thWidth140px}>Vigencia Hasta</th>
                    {tipoModificacion === 'variacion' && (
                      <>
                        <th className={styles.thWidth80px}>Op.</th>
                        <th className={styles.thWidth120px}>Var. %</th>
                        <th className={styles.thWidth140px}>Precio Final (sin iva)</th>
                      </>
                    )}
                    {tipoModificacion !== 'variacion' && (
                      <>
                        <th className={styles.thWidth100px}>Descuento %</th>
                        <th className={styles.thWidth140px}>Oferta Desde</th>
                        <th className={styles.thWidth140px}>Oferta Hasta</th>
                        <th className={styles.thWidth120px}>Precio c/ Desc.</th>
                        <th className={styles.thWidth120px}>Tipo de descuento aplicado</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const basePrecio = getPrecioBase(item)
                    const precioActualStr =
                      basePrecio !== null ? `$${basePrecio.toFixed(DECIMAL_PLACES)}` : '-'
                    let vigentePub = false
                    let porcDctoMostrar = 0
                    const desdePub = item.ofertaDesde ? new Date(item.ofertaDesde) : null
                    const hastaPub = item.ofertaHasta ? new Date(item.ofertaHasta) : null
                    const hoy = new Date()
                    if (
                      item.porcDcto > 0 &&
                      desdePub &&
                      hastaPub &&
                      hoy >= desdePub &&
                      hoy <= hastaPub
                    ) {
                      vigentePub = true
                      porcDctoMostrar = item.porcDcto
                    } else if (valores.porcDcto > 0) {
                      porcDctoMostrar = Number(valores.porcDcto)
                    } else {
                      porcDctoMostrar = item.porcDctoLista
                    }

                    let precioFinalStr = '-'
                    let variacionAplicar = valores.variacionPrecio

                    if (tipoModificacion === 'variacion' && basePrecio !== null) {
                      if (variacionesIndividuales[item.idPublicacion] !== undefined) {
                        variacionAplicar = variacionesIndividuales[item.idPublicacion]
                      }

                      if (variacionAplicar) {
                        const variacionNum = Number(variacionAplicar) || 0
                        const factorVariacion =
                          valores.operadorVariacion === '-'
                            ? -variacionNum / 100
                            : variacionNum / 100
                        const precioFinal = basePrecio * (1 + factorVariacion)
                        precioFinalStr = `$${precioFinal.toFixed(DECIMAL_PLACES)}`
                      }
                    }

                    let precioConDescuentoStr = '-'
                    if (basePrecio !== null) {
                      let descuentoAplicar = null

                      descuentoAplicar =
                        tipoModificacion === 'descuento'
                          ? porcDctoMostrar
                          : (item.porcDctoPublicacion ?? item.porcDctoLista)

                      if (descuentoAplicar) {
                        const precioConDescuento = basePrecio * (1 - descuentoAplicar / 100)
                        precioConDescuentoStr = `$${precioConDescuento.toFixed(DECIMAL_PLACES)}`
                      }
                    }

                    return (
                      <tr key={item.idPublicacion}>
                        <td>{item.idPublicacion}</td>
                        <td>{item.titulo}</td>
                        <td>{precioActualStr}</td>
                        <td>{item.hasta ? formatearFecha(item.hasta, 'DD/MM/YYYY') : '-'}</td>
                        {tipoModificacion === 'variacion' && (
                          <>
                            <td>
                              <Form.Select
                                size="sm"
                                value={valores.operadorVariacion}
                                disabled
                                readOnly
                              >
                                <option value="+">+</option>
                                <option value="-">-</option>
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                size="sm"
                                value={
                                  variacionesIndividuales[item.idPublicacion] !== undefined
                                    ? variacionesIndividuales[item.idPublicacion]
                                    : valores.variacionPrecio
                                }
                                onChange={(e) =>
                                  handleVariacionIndividual(item.idPublicacion, e.target.value)
                                }
                                placeholder="Usar global"
                              />
                            </td>
                            <td>{precioFinalStr}</td>
                          </>
                        )}
                        {tipoModificacion !== 'variacion' && (
                          <>
                            <td>
                              {vigentePub ? (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip id={`tooltip-descuento-${item.idPublicacion}`}>
                                      Esta publicación ya tiene un descuento del {porcDctoMostrar}%.
                                      Modifíquelo individualmente en Ventas - Administrar
                                    </Tooltip>
                                  }
                                >
                                  <div>
                                    <Form.Control
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max="100"
                                      size="sm"
                                      value={porcDctoMostrar}
                                      readOnly
                                      disabled
                                      style={{ color: 'red', fontWeight: 'bold' }}
                                    />
                                  </div>
                                </OverlayTrigger>
                              ) : (
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  size="sm"
                                  value={porcDctoMostrar}
                                  readOnly
                                  disabled
                                />
                              )}
                            </td>
                            <td>
                              <Form.Control
                                type="datetime-local"
                                size="sm"
                                value={
                                  vigentePub && desdePub
                                    ? formatearFecha(item.ofertaDesde, DATE_TIME_FORMAT)
                                    : valores.ofertaDesde
                                }
                                readOnly
                                disabled
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="datetime-local"
                                size="sm"
                                value={
                                  vigentePub && hastaPub
                                    ? formatearFecha(item.ofertaDesde, DATE_TIME_FORMAT)
                                    : valores.ofertaHasta
                                }
                                readOnly
                                disabled
                              />
                            </td>
                            <td>{precioConDescuentoStr}</td>
                            <td>
                              {vigentePub ? 'Descuento de publicación' : 'Descuento de lista'}
                            </td>
                          </>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <div className="alert alert-info">No hay publicaciones asociadas a esta lista.</div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

ModalEditarItemsLista.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  listaVenta: PropTypes.shape({
    idListaVenta: PropTypes.number.isRequired,
    idVendedor: PropTypes.number,
    idListaVentaEstado: PropTypes.number,
    nombre: PropTypes.string,
    vigenciaDesde: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    vigenciaHasta: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    porcDcto: PropTypes.number,
    ofertaDesde: PropTypes.string,
    ofertaHasta: PropTypes.string,
    variacionPrecio: PropTypes.number,
    signo: PropTypes.number,
    variacionPrecioDesde: PropTypes.string,
    variacionPrecioHasta: PropTypes.string
  }).isRequired,
  onActualizar: PropTypes.func,
  idVendedor: PropTypes.number
}
